import { fetchAndCache } from '@prismamedia/one-components';
import { PDFDocument } from 'pdf-lib';
import { fetchWithAuth } from 'utils/fetch';
import {
  useGetCenshareRailroadByName,
  useGetPrintIssue,
  usePrintIssue,
} from './issuePage.topic.graphql';
import { HalfPage, Sheet } from './Railroad/utils';

export const useGetPdf = () => {
  const getPrintIssue = useGetPrintIssue();

  return async (printIssueId: string) => {
    const { printIssue } = await getPrintIssue({ where: { id: printIssueId } });

    const brandKey = printIssue.printPublication.key;

    const res = await fetchWithAuth(
      `${config.API_ONE_TOPIC}/downloadPdf/${brandKey}/${printIssue.title}`,
      {
        headers: {
          'Content-Type': 'application/pdf',
        },
      },
    );

    if (!res.ok) throw new Error(`Error ${res.status} (${res.statusText})`);

    const blob = await res.blob();

    return {
      blob,
      title: `${brandKey}_${printIssue.title}.pdf`,
    };
  };
};

/**
 * Validates if a page has a valid filename for PDF generation
 */
const isValidPage = (page: HalfPage): boolean => {
  return !!page.filename && page.filename.trim() !== '';
};

/**
 * Downloads an image from a URL and returns it as ArrayBuffer
 */
const downloadImageAsArrayBuffer = async (
  url: string,
): Promise<ArrayBuffer> => {
  const response = await fetchWithAuth(url, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(
      `Error downloading image: ${response.status} ${response.statusText}`,
    );
  }

  return await response.arrayBuffer();
};

/**
 * Generates PDF from railroad pages using pdf-lib (same approach as CDF project)
 */
export const downloadFlatplanPdf = async (
  brandKey: string,
  issue: string,
  pages: HalfPage[] | undefined,
): Promise<{ blob: Blob; title: string }> => {
  if (!pages || pages.length === 0) {
    throw new Error('No pages available for PDF generation');
  }

  // Filter pages with valid filenames
  const validPages = pages.filter(isValidPage);

  if (validPages.length === 0) {
    throw new Error('No valid pages available for PDF generation');
  }

  // Create a new PDF document
  const pdfDoc = await PDFDocument.create();

  // Sort pages by page number (using native sort for better performance)
  // eslint-disable-next-line fp/no-mutating-methods
  const sortedPages = [...validPages].sort((a, b) => {
    const numA = parseInt(a.pageNumber || '0', 10) || 0;
    const numB = parseInt(b.pageNumber || '0', 10) || 0;
    return numA - numB;
  });

  // Number of concurrent downloads (increased for better performance)
  const CONCURRENT_DOWNLOADS = 20;

  // Cache base URL to avoid repeated access
  const baseUrl: string = config.API_ONE_TOPIC;

  // Interface for download results
  interface DownloadResult {
    image: Awaited<ReturnType<typeof pdfDoc.embedJpg>> | null;
    index: number;
    pageNumber: string;
    error?: unknown;
  }

  interface ValidDownloadResult {
    image: Awaited<ReturnType<typeof pdfDoc.embedJpg>>;
    index: number;
    pageNumber: string;
  }

  const isValidDownloadResult = (
    result: DownloadResult | null,
  ): result is ValidDownloadResult => {
    return result !== null && result.image !== null;
  };

  // Function to download and prepare an image
  const downloadPage = async (
    page: HalfPage,
    index: number,
  ): Promise<DownloadResult> => {
    try {
      // We already filtered pages with valid filenames, so filename is guaranteed to exist
      const { filename } = page;
      // Build URL - baseUrl is cached above
      const imageUrl = `${baseUrl}${filename}`;
      const imageBytes = await downloadImageAsArrayBuffer(imageUrl as string);

      // Detect image type (JPEG or PNG)
      const uint8View = new Uint8Array(imageBytes, 0, 3);
      const isJpeg =
        uint8View[0] === 0xff && uint8View[1] === 0xd8 && uint8View[2] === 0xff;

      // Embed the image
      const image = isJpeg
        ? await pdfDoc.embedJpg(imageBytes)
        : await pdfDoc.embedPng(imageBytes);

      return {
        image,
        index,
        pageNumber: page.pageNumber || '',
      };
    } catch (error) {
      return {
        image: null,
        index,
        pageNumber: page.pageNumber || '',
        error,
      };
    }
  };

  // Download queue with worker pool
  const results: (DownloadResult | null)[] = new Array(sortedPages.length).fill(
    null,
  );
  // eslint-disable-next-line fp/no-let
  let nextIndex = 0;

  const getNextIndex = (): number | null => {
    if (nextIndex >= sortedPages.length) return null;
    // eslint-disable-next-line fp/no-mutation, no-plusplus
    return nextIndex++;
  };

  // Function to add pages to PDF in order (first page alone, middle pages two by two, last page alone)
  const addOrderedPages = () => {
    // All pages are downloaded at this point (function is called after Promise.all completes)
    const validResults = results.filter(isValidDownloadResult);

    if (validResults.length === 0) return;

    // Calculate standard dimensions using median (using native sort for better performance)
    // eslint-disable-next-line fp/no-mutating-methods
    const widths = validResults.map((r) => r.image.width).sort((a, b) => a - b);
    // eslint-disable-next-line fp/no-mutating-methods
    const heights = validResults
      .map((r) => r.image.height)
      .sort((a, b) => a - b);
    const standardWidth = widths[Math.floor(widths.length / 2)];
    const standardHeight = heights[Math.floor(heights.length / 2)];

    // Helper function to scale image preserving aspect ratio
    const getScaledDimensions = (
      imageWidth: number,
      imageHeight: number,
      targetWidth: number,
      targetHeight: number,
    ) => {
      const widthRatio = targetWidth / imageWidth;
      const heightRatio = targetHeight / imageHeight;
      const ratio = Math.min(widthRatio, heightRatio);

      return {
        width: imageWidth * ratio,
        height: imageHeight * ratio,
        x: (targetWidth - imageWidth * ratio) / 2,
        y: (targetHeight - imageHeight * ratio) / 2,
      };
    };

    // Case 1: Single page
    if (validResults.length === 1) {
      const [result] = validResults;
      const pageObj = pdfDoc.addPage([result.image.width, result.image.height]);
      pageObj.drawImage(result.image, {
        x: 0,
        y: 0,
        width: result.image.width,
        height: result.image.height,
      });
      return;
    }

    // Case 2: Multiple pages
    // First page alone
    const [firstResult] = validResults;
    const firstPage = pdfDoc.addPage([
      firstResult.image.width,
      firstResult.image.height,
    ]);
    firstPage.drawImage(firstResult.image, {
      x: 0,
      y: 0,
      width: firstResult.image.width,
      height: firstResult.image.height,
    });

    // Middle pages two by two
    const middlePages = validResults.slice(1, -1);
    const processMiddlePages = (index: number): void => {
      if (index >= middlePages.length) return;

      const leftPage = middlePages[index];
      const rightPage = middlePages[index + 1];

      if (rightPage) {
        // Double page: two images side by side
        const doublePageWidth = standardWidth * 2;
        const doublePageHeight = standardHeight;
        const doublePage = pdfDoc.addPage([doublePageWidth, doublePageHeight]);

        // Left image - normalized to standard size
        const leftScaled = getScaledDimensions(
          leftPage.image.width,
          leftPage.image.height,
          standardWidth,
          standardHeight,
        );
        doublePage.drawImage(leftPage.image, {
          x: leftScaled.x,
          y: leftScaled.y,
          width: leftScaled.width,
          height: leftScaled.height,
        });

        // Right image - normalized to standard size
        const rightScaled = getScaledDimensions(
          rightPage.image.width,
          rightPage.image.height,
          standardWidth,
          standardHeight,
        );
        doublePage.drawImage(rightPage.image, {
          x: standardWidth + rightScaled.x,
          y: rightScaled.y,
          width: rightScaled.width,
          height: rightScaled.height,
        });
      } else {
        // Odd page alone (last page of middle)
        const singlePage = pdfDoc.addPage([
          leftPage.image.width,
          leftPage.image.height,
        ]);
        singlePage.drawImage(leftPage.image, {
          x: 0,
          y: 0,
          width: leftPage.image.width,
          height: leftPage.image.height,
        });
      }

      processMiddlePages(index + 2);
    };

    processMiddlePages(0);

    // Last page alone (if exists and not already processed)
    if (validResults.length > 1) {
      const lastResult = validResults[validResults.length - 1];
      const lastPage = pdfDoc.addPage([
        lastResult.image.width,
        lastResult.image.height,
      ]);
      lastPage.drawImage(lastResult.image, {
        x: 0,
        y: 0,
        width: lastResult.image.width,
        height: lastResult.image.height,
      });
    }
  };

  // Process pages with concurrent downloads
  const processNext = async (): Promise<void> => {
    const processIndex = async (): Promise<void> => {
      const index = getNextIndex();
      if (index === null) return;

      const result = await downloadPage(sortedPages[index], index);
      // eslint-disable-next-line immutable/no-mutation
      results[index] = result;
      await processIndex();
    };

    await processIndex();
  };

  // Start multiple workers in parallel
  await Promise.all(
    Array.from(
      { length: Math.min(CONCURRENT_DOWNLOADS, sortedPages.length) },
      () => processNext(),
    ),
  );

  // Ensure all pages are added
  addOrderedPages();

  // Generate the PDF
  const pdfBytes = await pdfDoc.save();

  // Create blob and return
  // pdfBytes is Uint8Array which is compatible with Blob
  // Convert to ArrayBuffer to satisfy TypeScript's strict type checking
  const arrayBuffer = pdfBytes.buffer.slice(
    pdfBytes.byteOffset,
    pdfBytes.byteOffset + pdfBytes.byteLength,
  );
  // Type assertion: slice returns ArrayBuffer | SharedArrayBuffer, but we know it's ArrayBuffer
  const blob = new Blob([arrayBuffer as ArrayBuffer], {
    type: 'application/pdf',
  });

  return {
    blob,
    title: `${brandKey}_${issue}.pdf`,
  };
};

export const useGetRailRoadPages = (printIssueId: string | undefined) => {
  const { data: printIssueData } = usePrintIssue(
    {
      where: { id: printIssueId },
    },
    !printIssueId,
  );

  const brandKey = printIssueData?.printIssue.printPublication.key || '';
  const issue = printIssueData?.printIssue.title || '';

  const { data, loading, error } = useGetCenshareRailroadByName(
    { brandKey, issue },
    !brandKey || !issue,
  );

  let orderedSheets: Sheet[] = [];
  try {
    const sheets = JSON.parse(data?.getCenshareRailroadByName?.sheets || '[]');

    // eslint-disable-next-line fp/no-mutating-methods
    const firstHalfOfIssueSheets = [...sheets].sort(
      (sheetA, sheetB) =>
        sheetA.traits.sortable.sorting - sheetB.traits.sortable.sorting,
    );

    // eslint-disable-next-line fp/no-mutating-methods
    const lastHalfOfIssueSheets = [...sheets].sort(
      (sheetA, sheetB) =>
        sheetB.traits.sortable.sorting - sheetA.traits.sortable.sorting,
    );

    orderedSheets = [
      ...firstHalfOfIssueSheets,
      ...lastHalfOfIssueSheets,
    ].reduce<Sheet[]>(
      (prev, sheet) => [
        ...prev,
        ...new Array(sheet.traits.sheet_info.firstHalfPages).fill({
          id: sheet.self,
          name: sheet.traits.display.name,
          color: sheet.traits.display.color,
        }),
      ],
      [],
    );
  } catch (e) {}

  const pages = data?.getCenshareRailroadByName?.pages?.reduce<HalfPage[]>(
    (prev, page, index) => {
      let parsedFilenames: { page: number; url: string }[] = [];
      try {
        parsedFilenames = JSON.parse(page?.filename || '');
      } catch (e) {}

      const filenamePage =
        (prev.filter(
          ({ indesignFilename }) => indesignFilename === page?.indesignFilename,
        )?.length || 0) + 1;

      const filename = parsedFilenames.find(
        (parsedFilename) => parsedFilename.page === filenamePage,
      );

      return [
        ...prev,
        {
          ...page,
          sheet: orderedSheets[index],
          filename: filename?.url,
        } as HalfPage,
      ];
    },
    [],
  );

  return {
    ...data?.getCenshareRailroadByName,
    pages,
    loading,
    error,
    brandKey,
    issue,
  };
};

export const getRailroadImage = async (filename: string) => {
  const res = await fetchAndCache(`${config.API_ONE_TOPIC}${filename}`);
  return res.blob();
};
